import clsx from "clsx";
import { useId, useState } from "react";

import { PopOver } from "@tsmono/react/components";
import { useEventListener } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import type { ThemePreference } from "@tsmono/theme/bootstrap";

import styles from "./ThemeToggle.module.css";

export interface ThemeToggleProps {
  value: ThemePreference;
  /** Resolved dark/light, so the button can show the current mode. */
  isDark: boolean;
  onChange: (value: ThemePreference) => void;
  className?: string;
  /**
   * Hide the light/dark mode radios — used inside VS Code/Cursor where the
   * host owns base mode and the viewer can only safely flip Event Colors.
   */
  hideModeSwitch?: boolean;
}

type ThemeMode = "system" | "light" | "dark";

/**
 * Header control: clicking opens the full theme menu. Purely presentational —
 * the host owns persistence.
 */
export const ThemeToggle = ({
  value,
  isDark,
  onChange,
  className,
  hideModeSwitch,
}: ThemeToggleProps) => {
  const id = useId();
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  // `base` is the persisted preference's mode axis (may be `system`).
  // Splitting it from the Event Colors flag keeps "follow OS / host" intact
  // when Event Colors is toggled.
  const stripped = value.startsWith("readable-")
    ? value.slice("readable-".length)
    : value;
  const base: ThemeMode =
    stripped === "light" || stripped === "dark" ? stripped : "system";
  const eventColors = value.startsWith("readable-");

  const setPreference = (
    nextBase: ThemeMode,
    nextEventColors = eventColors
  ) => {
    onChange(nextEventColors ? `readable-${nextBase}` : nextBase);
  };

  useEventListener(
    open && typeof document !== "undefined" ? document : null,
    "keydown",
    (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonEl?.focus();
      }
    }
  );

  return (
    // data-testid: stable hook for automated tests and for downstream
    // hosts that embed the viewer and want to hide the picker (e.g. an
    // outer app that owns its own theme control). The aria-label on the
    // button below is the only other stable handle and is subject to
    // localization, so external code should prefer this.
    <div className={clsx(styles.root, className)} data-testid="theme-toggle">
      <button
        ref={setButtonEl}
        type="button"
        className={styles.button}
        aria-label="Choose theme"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon
          name={isDark ? "themeDark" : "themeLight"}
          className={styles.icon}
        />
      </button>
      <PopOver
        id="theme-toggle-popover"
        isOpen={open}
        setIsOpen={setOpen}
        positionEl={buttonEl}
        placement="bottom-end"
        showArrow={false}
        hoverDelay={-1}
        closeOnMouseLeave={false}
        offset={[0, 1]}
        className={styles.menu}
      >
        <div className={styles.headerRow}>
          <b>Theme</b>
          <button
            type="button"
            className={styles.resetButton}
            // Reset returns to the default, which includes Event Colors.
            onClick={() => onChange("readable-system")}
          >
            Reset
          </button>
        </div>
        <fieldset className={styles.group} hidden={hideModeSwitch}>
          <legend className={styles.srOnly}>Theme mode</legend>
          {(["system", "light", "dark"] as const).map((themeMode) => (
            <label key={themeMode} className={styles.option}>
              <input
                type="radio"
                name={`${id}-theme-mode`}
                value={themeMode}
                checked={base === themeMode}
                onChange={() => setPreference(themeMode)}
              />
              <span>
                {themeMode.charAt(0).toUpperCase() + themeMode.slice(1)}
              </span>
            </label>
          ))}
        </fieldset>
        <label className={styles.option}>
          <input
            type="checkbox"
            checked={eventColors}
            onChange={() => setPreference(base, !eventColors)}
          />
          <span>Event Colors</span>
        </label>
      </PopOver>
    </div>
  );
};
